import { makeAutoObservable } from 'mobx'
import type {
  IKnowledgeBaseDocumentSource,
  IResponseKnowledgeBaseDocument,
} from 'entities/KnowledgeBase'

export enum KnowledgeBaseDocumentStatus {
  Uploading = 0,
  UploadingSuccess = 1,
  UploadingFailed = 2,
  Draft = 99,
  Indexation = 100,
  IndexationSuccess = 101,
  IndexationFailed = 102,
  Complete = 200,
  Failed = 300,
}

export class KnowledgeBaseDocument {
  id: number
  name: string
  knowledgeBaseId: number
  status: KnowledgeBaseDocumentStatus
  contentType: string
  extension: string | null = null
  size: number | null = null
  uploadedAt: string
  createdAt: string
  source: IKnowledgeBaseDocumentSource
  importUrl: string
  updatedAt: string
  isLiveDocument: boolean
  documentId: string | null
  documentUrl: string | null
  lastFetchedAt: string | null
  errorMessage: string | null

  constructor(document: IResponseKnowledgeBaseDocument) {
    this.id = document.id
    this.name = document.name
    this.knowledgeBaseId = document.id
    this.status = document.status
    this.contentType = document.content_type
    this.extension = document.extension
    this.size = document.size
    this.uploadedAt = document.uploaded_at || ''
    this.createdAt = document.created_at || ''
    this.source = document.source
    this.importUrl = document.import_url || ''
    this.updatedAt = document.updated_at || ''
    this.isLiveDocument = document.is_live_document ?? false
    this.documentId = document.document_id ?? null
    this.documentUrl = document.document_url ?? null
    this.lastFetchedAt = document.last_fetched_at ?? null
    this.errorMessage = document.error_message ?? null

    makeAutoObservable(this)
  }

  getIsUploading = () => {
    return this.status === KnowledgeBaseDocumentStatus.Uploading
  }

  getIsUploadingSuccess = () => {
    return this.status === KnowledgeBaseDocumentStatus.UploadingSuccess
  }

  getIsUploadingFailed = () => {
    return this.status === KnowledgeBaseDocumentStatus.UploadingFailed
  }

  getIsIndexationFailed = () => {
    return this.status === KnowledgeBaseDocumentStatus.IndexationFailed
  }

  getIsIndexing = () => {
    return this.status === KnowledgeBaseDocumentStatus.Indexation
  }

  getIsAccessLost = () => {
    return this.isLiveDocument && this.status === KnowledgeBaseDocumentStatus.Failed
  }

  getExternalUrl = () => {
    return this.importUrl || this.documentUrl || ''
  }

  getIsLiveDocument = () => {
    return this.isLiveDocument
  }

  getIsGoogleSheet = () => {
    return this.source === 'google_sheet'
  }

  getIsGoogleDoc = () => {
    return this.source === 'google_doc'
  }

  getDocType = () => {
    switch (this.source) {
      case 'google_sheet':
      case 'google_doc':
      case 'web_url':
        return this.source
      default:
        return this.extension || this.contentType
    }
  }
}
